import { SummaryCard, DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';
import geoStyles from './GeographyViewer.module.css';

const codeToFlag = (code) => {
  if (!code || code.length !== 2) return '🌐';
  return [...code.toUpperCase()].map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65)).join('');
};

function parseMarkets(str) {
  if (!str) return [];
  return str.split(', ').map((entry) => {
    const match = entry.match(/^(.+?)\s*\(([^)]+)\)$/);
    return match ? { name: match[1].trim(), currency: match[2].trim() } : { name: entry.trim(), currency: null };
  });
}

function parseList(str) {
  if (!str) return [];
  return str.split(', ').map((s) => s.trim()).filter(Boolean);
}

const READINESS_COLORS = { High: geoStyles.readinessHigh, Medium: geoStyles.readinessMedium, Low: geoStyles.readinessLow };

const COUNTRY_NAME_TO_CODE = {
  'Saudi Arabia': 'SA', 'United Arab Emirates': 'AE', 'United States': 'US',
  'United Kingdom': 'GB', 'Germany': 'DE', 'France': 'FR', 'Canada': 'CA',
  'Australia': 'AU', 'India': 'IN', 'Japan': 'JP', 'China': 'CN',
  'Brazil': 'BR', 'Mexico': 'MX', 'Italy': 'IT', 'Spain': 'ES',
  'Netherlands': 'NL', 'Singapore': 'SG', 'Turkey': 'TR', 'Egypt': 'EG',
  'Kuwait': 'KW', 'Qatar': 'QA', 'Bahrain': 'BH', 'Oman': 'OM',
  'Jordan': 'JO', 'Lebanon': 'LB', 'Iraq': 'IQ', 'Pakistan': 'PK',
};

const GeographyViewer = ({ report }) => {
  const payload = usePayload(report);
  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));

  const readiness = byKey['international_readiness']?.value || null;
  const scope = byKey['geographic_scope']?.value || null;
  const countryCount = byKey['number_of_shipping_countries']?.value ?? null;
  const primaryCountry = byKey['primary_market_country']?.value || null;
  const marketsStr = byKey['active_markets']?.value || null;
  const currenciesStr = byKey['operating_currencies']?.value || null;
  const languagesStr = byKey['operating_languages']?.value || null;

  const markets = parseMarkets(marketsStr);
  const currencies = parseList(currenciesStr);
  const languages = parseList(languagesStr);

  const missing = Array.isArray(payload?.diagnostics?.missing_data_flags) ? payload.diagnostics.missing_data_flags : [];

  return (
    <div className={styles.viewer}>
      <SummaryCard summary={payload?.summary} />

      {/* Stats strip */}
      <div className={geoStyles.statsStrip}>
        {readiness && (
          <div className={geoStyles.statPill}>
            <span className={geoStyles.statPillLabel}>International Readiness</span>
            <span className={`${geoStyles.readinessBadge} ${READINESS_COLORS[readiness] || ''}`}>{readiness}</span>
          </div>
        )}
        {scope && (
          <div className={geoStyles.statPill}>
            <span className={geoStyles.statPillLabel}>Scope</span>
            <span className={geoStyles.statPillValue}>{scope}</span>
          </div>
        )}
        {countryCount != null && (
          <div className={geoStyles.statPill}>
            <span className={geoStyles.statPillLabel}>Countries</span>
            <span className={geoStyles.statPillValue}>{countryCount}</span>
          </div>
        )}
        {currencies.length > 0 && (
          <div className={geoStyles.statPill}>
            <span className={geoStyles.statPillLabel}>Currencies</span>
            <span className={geoStyles.statPillValue}>{currencies.length}</span>
          </div>
        )}
        {languages.length > 0 && (
          <div className={geoStyles.statPill}>
            <span className={geoStyles.statPillLabel}>Languages</span>
            <span className={geoStyles.statPillValue}>{languages.length}</span>
          </div>
        )}
      </div>

      {/* Market cards */}
      {markets.length > 0 && (
        <section>
          <p className={styles.sectionLabel}>Active markets</p>
          <div className={geoStyles.marketGrid}>
            {markets.map((m, i) => {
              const code = COUNTRY_NAME_TO_CODE[m.name] || null;
              const flag = code ? codeToFlag(code) : '🌐';
              const isPrimary = m.name === primaryCountry;
              return (
                <div key={i} className={`${geoStyles.marketCard} ${isPrimary ? geoStyles.marketCardPrimary : ''}`}>
                  <span className={geoStyles.marketFlag}>{flag}</span>
                  <div className={geoStyles.marketInfo}>
                    <span className={geoStyles.marketName}>{m.name}</span>
                    {isPrimary && <span className={geoStyles.marketPrimaryTag}>Primary</span>}
                  </div>
                  {m.currency && <span className={geoStyles.marketCurrency}>{m.currency}</span>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Currencies + Languages */}
      {(currencies.length > 0 || languages.length > 0) && (
        <div className={geoStyles.pillsRow}>
          {currencies.length > 0 && (
            <section className={geoStyles.pillsGroup}>
              <p className={styles.sectionLabel}>Currencies</p>
              <div className={geoStyles.pills}>
                {currencies.map((c) => <span key={c} className={geoStyles.pill}>{c}</span>)}
              </div>
            </section>
          )}
          {languages.length > 0 && (
            <section className={geoStyles.pillsGroup}>
              <p className={styles.sectionLabel}>Languages</p>
              <div className={geoStyles.pills}>
                {languages.map((l) => <span key={l} className={geoStyles.pill}>{l}</span>)}
              </div>
            </section>
          )}
        </div>
      )}

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default GeographyViewer;
